import {
  CreatePropertyDto,
  PropertiesControllerAssignManagerData,
  PropertiesControllerAssignOwnerData,
  PropertiesControllerCreateData,
  PropertiesControllerDeleteImageData,
  PropertiesControllerFindAllData,
  PropertiesControllerFindByManagerData,
  PropertiesControllerFindByOwnerData,
  PropertiesControllerFindOneData,
  PropertiesControllerRemoveData,
  PropertiesControllerRemoveManagerData,
  PropertiesControllerRemoveOwnerData,
  PropertiesControllerReorderImagesData,
  PropertiesControllerUpdateData,
  PropertiesControllerUpdateStatusData,
  PropertiesControllerUploadImagesData,
  UpdatePropertyDto,
  ContentType
} from '@/common/api/datasources/Api';
import { api } from '@/common/api/instance';

interface PaginationParams {
  page?: number;
  size?: number;
  search?: string;
  type?: 'apartment' | 'house' | 'office' | 'commercial' | 'land';
  status?: 'available' | 'occupied' | 'sold' | 'pending' | 'maintenance';
  minPrice?: number;
  maxPrice?: number;
  currency?: 'USD';
  city?: string;
  minArea?: number;
  maxArea?: number;
  minRooms?: number;
  maxRooms?: number;
  features?: string[];
  radius?: number;
  latitude?: string;
  longitude?: string;
  sortBy?: 'price' | 'createdAt' | 'area' | 'rooms';
  sortOrder?: 'ASC' | 'DESC';
}

export const propertiesService = {
  createProperty: async (data: CreatePropertyDto): Promise<PropertiesControllerCreateData> => {
    const response = await api.properties.propertiesControllerCreate(data);
    return response.data;
  },

  getAllProperties: async (params: PaginationParams): Promise<PropertiesControllerFindAllData> => {
    const response = await api.properties.propertiesControllerFindAll(params);
    return response.data;
  },

  getPropertyByUuid: async (uuid: string): Promise<PropertiesControllerFindOneData> => {
    const response = await api.properties.propertiesControllerFindOne(uuid);
    return response.data;
  },

  updateProperty: async (uuid: string, data: UpdatePropertyDto): Promise<PropertiesControllerUpdateData> => {
    const response = await api.properties.propertiesControllerUpdate(uuid, data);
    return response.data;
  },

  deleteProperty: async (uuid: string): Promise<PropertiesControllerRemoveData> => {
    const response = await api.properties.propertiesControllerRemove(uuid);
    return response.data;
  },

  updateStatus: async (uuid: string, status: string): Promise<PropertiesControllerUpdateStatusData> => {
    const response = await api.properties.propertiesControllerUpdateStatus(uuid);
    return response.data;
  },

  findByOwner: async (ownerUUID: string): Promise<PropertiesControllerFindByOwnerData> => {
    const response = await api.properties.propertiesControllerFindByOwner(ownerUUID);
    return response.data;
  },

  findByManager: async (managerUUID: string): Promise<PropertiesControllerFindByManagerData> => {
    const response = await api.properties.propertiesControllerFindByManager(managerUUID);
    return response.data;
  },

  assignManager: async (uuid: string, managerUUID: string): Promise<PropertiesControllerAssignManagerData> => {
    const response = await api.properties.propertiesControllerAssignManager(uuid, managerUUID);
    return response.data;
  },

  removeManager: async (uuid: string): Promise<PropertiesControllerRemoveManagerData> => {
    const response = await api.properties.propertiesControllerRemoveManager(uuid);
    return response.data;
  },

  assignOwner: async (uuid: string, ownerUUID: string): Promise<PropertiesControllerAssignOwnerData> => {
    const body = { ownerUUID };
    const response = await api.properties.propertiesControllerAssignOwner(uuid, {
      transformRequest: [(data, headers) => {
        headers['Content-Type'] = 'application/json';
        return JSON.stringify(body);
      }]
    });
    return response.data;
  },

  removeOwner: async (uuid: string): Promise<PropertiesControllerRemoveOwnerData> => {
    const response = await api.properties.propertiesControllerRemoveOwner(uuid);
    return response.data;
  },

  uploadImages: async (uuid: string, files: File[], startOrder: number = 0): Promise<PropertiesControllerUploadImagesData> => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    formData.append('startOrder', startOrder.toString());

    const response = await api.properties.propertiesControllerUploadImages(
      uuid,
      {
        files,
        startOrder
      },
      {
        transformRequest: [(data, headers) => {
          headers['Content-Type'] = 'multipart/form-data';
          return formData;
        }]
      }
    );
    return response.data;
  },

  reorderImages: async (uuid: string, imageOrders: { imageUUID: string; order: number }[]): Promise<PropertiesControllerReorderImagesData> => {
    const response = await api.properties.propertiesControllerReorderImages(uuid, { imageOrders });
    return response.data;
  },

  deleteImage: async (uuid: string, imageUUID: string): Promise<PropertiesControllerDeleteImageData> => {
    const response = await api.properties.propertiesControllerDeleteImage(uuid, imageUUID);
    return response.data;
  },

  deleteAllImages: async (uuid: string): Promise<PropertiesControllerDeleteImageData> => {
    const response = await api.properties.propertiesControllerDeleteAllImages(uuid);
    return response.data;
  },
};

export const usePropertiesQuery = () => ({
  allPropertiesQuery: (params: PaginationParams) => ({
    queryKey: ['properties', params],
    queryFn: () => propertiesService.getAllProperties(params),
  }),

  propertyByIdQuery: (uuid: string) => ({
    queryKey: ['properties', uuid],
    queryFn: () => propertiesService.getPropertyByUuid(uuid),
  }),

  propertiesByOwnerQuery: (ownerUUID: string) => ({
    queryKey: ['properties', 'owner', ownerUUID],
    queryFn: () => propertiesService.findByOwner(ownerUUID),
  }),

  propertiesByManagerQuery: (managerUUID: string) => ({
    queryKey: ['properties', 'manager', managerUUID],
    queryFn: () => propertiesService.findByManager(managerUUID),
  }),

  createPropertyMutation: {
    mutationFn: (data: CreatePropertyDto) => propertiesService.createProperty(data),
  },

  updatePropertyMutation: {
    mutationFn: ({ uuid, data }: { uuid: string; data: UpdatePropertyDto }) =>
      propertiesService.updateProperty(uuid, data),
  },

  deletePropertyMutation: {
    mutationFn: (uuid: string) => propertiesService.deleteProperty(uuid),
  },

  updateStatusMutation: {
    mutationFn: ({ uuid, status }: { uuid: string; status: string }) =>
      propertiesService.updateStatus(uuid, status),
  },

  assignManagerMutation: {
    mutationFn: ({ uuid, managerUUID }: { uuid: string; managerUUID: string }) =>
      propertiesService.assignManager(uuid, managerUUID),
  },

  removeManagerMutation: {
    mutationFn: (uuid: string) => propertiesService.removeManager(uuid),
  },

  assignOwnerMutation: {
    mutationFn: ({ uuid, ownerUUID }: { uuid: string; ownerUUID: string }) =>
      propertiesService.assignOwner(uuid, ownerUUID),
  },

  removeOwnerMutation: {
    mutationFn: (uuid: string) => propertiesService.removeOwner(uuid),
  },

  uploadImagesMutation: {
    mutationFn: ({ uuid, files, startOrder = 0 }: { uuid: string; files: File[]; startOrder?: number }) =>
      propertiesService.uploadImages(uuid, files, startOrder),
  },

  reorderImagesMutation: {
    mutationFn: ({ uuid, imageOrders }: { uuid: string; imageOrders: { imageUUID: string; order: number }[] }) =>
      propertiesService.reorderImages(uuid, imageOrders),
  },

  deleteImageMutation: {
    mutationFn: ({ uuid, imageUUID }: { uuid: string; imageUUID: string }) =>
      propertiesService.deleteImage(uuid, imageUUID),
  },

  deleteAllImagesMutation: {
    mutationFn: (uuid: string) => propertiesService.deleteAllImages(uuid),
  },
}); 